import { useRef, useCallback, useEffect, useState } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { api, TOKEN_KEY, ADMIN_TOKEN_KEY, backToAdmin } from "@/lib/leuteliaApi";
import { setDemoInfo } from "@/lib/demo";

function ImpersonationBanner({ user }) {
  if (!localStorage.getItem(ADMIN_TOKEN_KEY)) return null;
  return (
    <div className="sticky top-0 z-[60] bg-brand-gold text-brand-greenDark text-[12.5px] font-semibold px-4 py-2 flex items-center justify-between gap-3" data-testid="impersonation-banner">
      <span>Deroga del titolare: stai vedendo LEUTELIA come <strong>{user.name}</strong> ({user.role === "director" ? "Direttore" : user.role}) — tutto ciò che vedi è esattamente ciò che vede lui.</span>
      <button onClick={backToAdmin} className="px-3 py-1 rounded-md bg-brand-greenDark text-brand-cream text-[12px]" data-testid="impersonation-back">Torna amministratore</button>
    </div>
  );
}
import Login from "@/leutelia/Login";
import ImpostaPassword from "@/leutelia/ImpostaPassword";
import Admin from "@/leutelia/Admin";
import AppTelefono from "@/leutelia/AppTelefono";
import Promemoria, { AccessiAccount } from "@/leutelia/Promemoria";
import Reparti from "@/leutelia/Reparti";
import Risultati from "@/leutelia/Risultati";
import Sidebar, { CASCADE } from "@/leutelia/Sidebar";
import Google from "@/leutelia/Google";
import CascadeModule from "@/leutelia/CascadeModule";
import FontiEsterne from "@/leutelia/FontiEsterne";
import Fotografia from "@/leutelia/Fotografia";
import { toast } from "sonner";
import Topbar from "@/leutelia/Topbar";
import Priorita from "./leutelia/Priorita";
import Panoramica from "@/leutelia/Panoramica";
import Analisi from "@/leutelia/Analisi";
import Onboarding from "@/leutelia/Onboarding";
import Recensioni from "@/leutelia/Recensioni";
import Materialita from "@/leutelia/Materialita";
import { Loader2, Construction } from "lucide-react";
import PacchettoBloccato, { moduleAllowed } from "@/leutelia/PacchettoBloccato";
import Pacchetti from "@/leutelia/Pacchetti";
import Gruppo from "@/leutelia/Gruppo";
import Legale, { isLegalPath } from "@/leutelia/Legale";
import Aiuto from "@/leutelia/Aiuto";
import Soluzioni from "@/leutelia/Soluzioni";
import RefertoCondiviso from "@/leutelia/RefertoCondiviso";
import { DemoStrip, AvvisoSmartphone } from "@/leutelia/DemoBanner";
import DemoAdmin from "@/leutelia/DemoAdmin";
import Benvenuto from "@/leutelia/Benvenuto";
import Concorrenti from "@/leutelia/Concorrenti";
import { RichiestaForm } from "@/leutelia/RichiestaAccesso";
import { AuthFrame } from "@/leutelia/Login";

const MODULES = {
  target: "Target", sito: "Analisi del sito", seo: "SEO", branding: "Branding",
  marketing: "Marketing", fotografia: "Fotografia", social: "Social", ota: "OTA",
  priorita: "Priorità", report: "Report", aiuto: "Aiuto",
};
const CASCADE_DESC = {
  target: "A chi è destinato l'hotel: alto/basso spendente, italiano/estero, leisure/business. Parte dalla Panoramica e orienta tutti i moduli successivi.",
  sito: "Analisi del sito (testi, H1/H2/H3, UX, conversione) alla luce del Target definito.",
  seo: "Keyword e visibilità organica coerenti con il sito analizzato e il pubblico di riferimento.",
  branding: "Identità, tono di voce e coerenza del brand rispetto a target, sito e SEO.",
  fotografia: "Fotografia allineata al posizionamento e al branding emersi a monte.",
  marketing: "Azioni di marketing che chiudono la cascata: canali, messaggi e priorità.",
};

function ModulePlaceholder({ id }) {
  const idx = CASCADE.findIndex((c) => c.id === id);
  const prev = idx > 0 ? CASCADE[idx - 1].label : idx === 0 ? "Panoramica" : null;
  return (
    <div className="relative z-10 px-8 py-16 max-w-3xl leu-in" data-testid={`module-${id}`}>
      <div className="flex items-center gap-3 mb-3">
        <Construction size={22} className="text-brand-gold" />
        <div className="text-brand-orange font-bold tracking-[0.22em] text-[11px] uppercase">
          {idx >= 0 ? `Intelligenza a cascata · 0${idx + 1}` : "Fonti esterne"} · {MODULES[id]}
        </div>
      </div>
      <h2 className="font-serif text-3xl text-brand-ink">Analisi specialistica in arrivo</h2>
      {idx >= 0 ? (
        <>
          <p className="text-[14.5px] text-brand-ink mt-3 leading-relaxed">{CASCADE_DESC[id]}</p>
          <p className="text-[13px] text-brand-muted mt-3 leading-relaxed" data-testid="cascade-hint">
            Parte da ciò che LEUTELIA sa dell'hotel e dal risultato del modulo precedente (<strong>{prev}</strong>).
          </p>
        </>
      ) : (
        <p className="text-[14.5px] text-brand-muted mt-3 leading-relaxed">
          Fonte esterna: ciò che gli altri dicono e mostrano del tuo hotel, letto e riportato in Panoramica. Google e Recensioni sono già attivi.
        </p>
      )}
    </div>
  );
}

export default function App() {
  const [auth, setAuth] = useState(undefined);
  const [demo, setDemo] = useState(undefined);
  useEffect(() => {
    const out = () => setAuth(null);
    window.addEventListener("leutelia:logout", out);
    api.demoInfo().then((d) => { setDemoInfo(d.demo ? d : null); setDemo(d.demo ? d : null); }).catch(() => { setDemoInfo({ demo: true, hotel_name: "Hotel Miramar Barcelona" }); setDemo({ demo: true, hotel_name: "Hotel Miramar Barcelona" }); });
    const wantsLogin = window.location.pathname.startsWith("/accesso");
    if (!localStorage.getItem(TOKEN_KEY) && wantsLogin) setAuth(null);
    else api.me().then(setAuth).catch(() => setAuth(null));
    return () => window.removeEventListener("leutelia:logout", out);
  }, []);
  const token = new URLSearchParams(window.location.search).get("token");
  if (isLegalPath(window.location.pathname)) return <Legale path={window.location.pathname} />;
  if (window.location.pathname.startsWith("/imposta-password") && token) return <><ImpostaPassword token={token} /><Toaster position="top-right" richColors /></>;
  if (window.location.pathname.startsWith("/richiedi-accesso")) return (
    <><AuthFrame title="Scopri cosa LEUTELIA può individuare nel tuo hotel" sub="Lascia i tuoi riferimenti: LEUTELIA ti scrive entro pochi giorni. Nessuna analisi parte senza accordo." testid="richiesta-page"><RichiestaForm /><a href="/" className="block mt-4 text-[12.5px] text-brand-muted hover:text-brand-green" data-testid="richiesta-torna-login">← Torna all'accesso</a></AuthFrame><Toaster position="top-right" richColors /></>
  );
  if (auth === undefined || demo === undefined) return <div className="min-h-screen bg-brand-cream flex items-center justify-center text-brand-muted"><Loader2 className="animate-spin text-brand-gold" /></div>;
  if (!auth) return <><Login onAuth={setAuth} demo={demo} /><Toaster position="top-right" richColors /></>;
  if (window.location.pathname.startsWith("/referto/")) return <><RefertoCondiviso token={window.location.pathname.split("/")[2] || ""} auth={auth} /><Toaster position="top-right" richColors /></>;
  if (["caporeparto", "dipendente"].includes(auth.user.role)) return <StaffWorkspace auth={auth} setAuth={setAuth} />;
  return <Workspace key={auth.user.email} auth={auth} setAuth={setAuth} demo={demo} />;
}

function StaffWorkspace({ auth, setAuth }) {
  const h = auth.hotels[0];
  const scope = h ? { tenant: h.tenant, hotel: h.hotel } : null;
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuth(null); };
  return (
    <div className="min-h-screen bg-brand-cream leutelia-grain" data-testid="staff-workspace">
      <ImpersonationBanner user={auth.user} />
      <Topbar ccu={{ canonical_identity: { name: h?.name } }} hotels={auth.hotels} scope={scope} user={auth.user} onLogout={logout} canSwitch={false} onMenu={() => {}} />
      <main>{scope ? <><div className="px-4 sm:px-8 pt-6 max-w-[1320px]"><AppTelefono /></div><Reparti scope={scope} user={auth.user} onNav={() => {}} /></> : <p className="p-10 text-brand-muted">Nessun hotel collegato al tuo accesso.</p>}</main>
      <Toaster position="top-right" richColors />
    </div>
  );
}

function Workspace({ auth, setAuth, demo }) {
  const [catalog, setCatalog] = useState([]);
  useEffect(() => { api.pacchetti().then((r) => setCatalog(r.items || [])).catch(() => {}); }, []);
  const user = auth.user;
  const isAdmin = user.role === "admin";
  const isDemo = !!demo;
  const canSwitch = isAdmin || (auth.settings?.multi_hotel && auth.hotels.length > 1);
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setAuth(null); };
  const [hotels, setHotels] = useState([]);
  const [scope, setScope] = useState(null);
  const [ccu, setCcu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nav, setNav] = useState("panoramica");
  const [onboarding, setOnboarding] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [soluzioniOn, setSoluzioniOn] = useState(null);
  useEffect(() => {
    if (!scope) return;
    const check = () => api.soluzioniStatus(scope.tenant, scope.hotel).then((s) => setSoluzioniOn(!!s.enabled)).catch(() => setSoluzioniOn(false));
    check();
    window.addEventListener("leutelia:soluzioni-feature", check);
    return () => window.removeEventListener("leutelia:soluzioni-feature", check);
  }, [scope?.tenant, scope?.hotel]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHotels = useCallback(async () => {
    const { hotels } = await api.listHotels();
    setHotels(hotels);
    return hotels;
  }, []);
  useEffect(() => { if (!isAdmin && hotels.length === 0 && !loading) toast.error("Nessun hotel assegnato al tuo account: contatta LEUTELIA."); }, [hotels, loading, isAdmin]);

  const ccuKey = useRef("");
  const loadCCU = useCallback(async (sc) => {
    if (!sc) return;
    const key = `${sc.tenant}/${sc.hotel}`; ccuKey.current = key;
    setLoading(true);
    try {
      const data = await api.getCCU(sc.tenant, sc.hotel);
      if (ccuKey.current === key) setCcu(data); // risposta di un hotel non più selezionato: scartata
    } finally { if (ccuKey.current === key) setLoading(false); }
  }, []);

  const booted = useRef(false);
  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    const q = new URLSearchParams(window.location.search);
    if (q.get("ntf")) api.notificaAperta(q.get("ntf")).catch(() => {});
    if (q.get("potatura") === "1") sessionStorage.setItem("leu_potatura", "1");
    (async () => {
      const hs = await loadHotels();
      const g = q.get("google");
      const m = q.get("meta");
      const wanted = hs.find((h) => h.tenant === q.get("tenant") && h.hotel === q.get("hotel"));
      const fromGoogle = (g === "connected" || m === "connected") && wanted;
      const def = fromGoogle || wanted || hs[0];
      if (def) { const sc = { tenant: def.tenant, hotel: def.hotel }; setScope(sc); await loadCCU(sc); }
      else setLoading(false);
      if (q.get("nav") && !g && !m) { setNav(q.get("nav")); window.history.replaceState({}, "", window.location.pathname); }
      if (m) {
        setNav(q.get("target") === "admin" ? "admin" : "social");
        if (m === "connected") toast.success("Facebook e Instagram collegati: premi «Leggi ora».");
        else toast.error(m === "denied" ? "Autorizzazione Facebook annullata." : `Collegamento Meta non riuscito (${q.get("reason") || "errore"}).`);
        window.history.replaceState({}, "", window.location.pathname);
      }
      if (g) {
        setNav("google");
        if (g === "connected") toast.success("Account Google collegato: scegli la sede della scheda.");
        else toast.error(g === "denied" ? "Autorizzazione Google annullata." : `Collegamento Google non riuscito (${q.get("reason") || "errore"}).`);
        window.history.replaceState({}, "", window.location.pathname);
      }
    })();
  }, [loadHotels, loadCCU]);

  const selectHotel = async (val) => {
    const [tenant, hotel] = val.split("::");
    const sc = { tenant, hotel };
    setScope(sc); setNav("panoramica"); await loadCCU(sc);
  };

  const onOnboarded = async (newScope) => {
    setOnboarding(false);
    await loadHotels();
    setScope(newScope); setNav("panoramica");
    await loadCCU(newScope);
  };

  const reload = () => loadCCU(scope);
  useEffect(() => { if (isDemo && !isAdmin) api.demoVista(String(nav)).catch(() => {}); }, [nav, isDemo, isAdmin]);

  const renderModule = () => {
    if (nav === "admin" && isAdmin && isDemo) return <DemoAdmin />;
    if (!isAdmin && !moduleAllowed(auth?.settings, nav)) return <PacchettoBloccato module={String(nav).split(":")[0]} settings={auth?.settings} catalog={catalog} />;
    if (nav === "panoramica") return <Panoramica ccu={ccu} scope={scope} reload={reload} onNav={setNav} />;
    if (nav === "analisi") return <Analisi ccu={ccu} scope={scope} reload={reload} onNav={setNav} />;
    if (nav === "recensioni") return <Recensioni ccu={ccu} scope={scope} />;
    if (nav === "concorrenti") return <Concorrenti ccu={ccu} scope={scope} />;
    if (nav === "priorita") return <Priorita ccu={ccu} scope={scope} onNav={setNav} soluzioniOn={soluzioniOn} />;
    if (String(nav).startsWith("soluzioni") && soluzioniOn === null) return <div className="p-10 text-brand-muted text-[13px]">Stiamo preparando le schede…</div>;
    if (String(nav).startsWith("soluzioni") && soluzioniOn) return <Soluzioni ccu={ccu} scope={scope} onNav={setNav} focusId={String(nav).split(":").slice(1).join(":") || null} />;
    if (nav === "google") return <Google ccu={ccu} scope={scope} reload={reload} />;
    if (nav === "fotografia") return <Fotografia ccu={ccu} scope={scope} />;
    if (CASCADE.some((c) => c.id === nav)) return <CascadeModule id={nav} ccu={ccu} scope={scope} />;
    if (nav === "ota" || nav === "social") return <FontiEsterne id={nav} ccu={ccu} scope={scope} reload={reload} />;
    if (nav === "pacchetti") return <Pacchetti catalog={catalog} settings={auth?.settings} isAdmin={isAdmin} />;
    if (nav === "gruppo") return <Gruppo scope={scope} onSelectHotel={(h) => selectHotel(`${scope.tenant}::${h}`)} />;
    if (nav === "aiuto") return <Aiuto user={user} />;
    if (nav === "impostazioni") return (
      <div className="relative z-10 px-4 sm:px-8 py-7 max-w-[1320px] 2xl:max-w-[1560px] min-[2000px]:max-w-[1760px] leu-in">
        <div className="text-brand-orange font-bold tracking-[0.22em] text-[11px] uppercase mb-3">Impostazioni</div>
        <AppTelefono />
        <Promemoria user={user} onUser={(u) => setAuth({ ...auth, user: u })} />
        <AccessiAccount />
        <Materialita ccu={ccu} scope={scope} reload={reload} />
      </div>
    );
    if (nav === "risultati") return <Risultati scope={scope} />;
    if (nav === "reparti" || nav.startsWith("reparto:")) { const parts = nav.split(":"); return <Reparti scope={scope} user={user} dept={parts[1] || null} area={parts[2] ? decodeURIComponent(parts[2]) : ""} onNav={setNav} />; }
    if (nav === "admin" && isAdmin) return <Admin onHotelsChanged={loadHotels} onAddHotel={() => setOnboarding(true)} />;
    return <ModulePlaceholder id={nav} />;
  };

  return (
    <div className="flex min-h-screen bg-brand-cream leutelia-grain">
      <Sidebar active={nav} onNav={(id) => { setNav(id); setMenuOpen(false); }} open={menuOpen} onClose={() => setMenuOpen(false)} isAdmin={isAdmin} scope={scope} settings={auth?.settings} soluzioniOn={soluzioniOn} demo={isDemo} showGruppo={!isDemo && (isAdmin ? hotels.filter((h) => h.tenant === scope?.tenant).length > 1 : hotels.length > 1)} />
      <div className="flex-1 min-w-0 flex flex-col">
        {isDemo && <DemoStrip info={demo} />}
        <ImpersonationBanner user={user} />
        {(scope || isAdmin) && (
          <Topbar ccu={ccu} hotels={hotels} scope={scope} onMenu={() => setMenuOpen(true)} user={user} onLogout={logout} canSwitch={canSwitch} demo={isDemo}
            onSelectHotel={selectHotel} onAddHotel={isAdmin && !isDemo ? () => setOnboarding(true) : null} />
        )}
        <main className="flex-1 min-w-0">
          {nav === "admin" && isAdmin ? renderModule() : loading || !ccu ? (
            <div className="flex items-center gap-3 justify-center h-[70vh] text-brand-muted">
              {!loading && !scope ? <span className="text-[14px]">{isAdmin ? "Nessun hotel: aggiungilo da Amministrazione." : "Nessun hotel assegnato al tuo account."}</span>
                : <><Loader2 className="animate-spin text-brand-gold" /> <span className="font-mono text-[13px]">stiamo leggendo il tuo hotel…</span></>}
            </div>
          ) : renderModule()}
        </main>
      </div>
      {onboarding && <Onboarding onDone={onOnboarded} onClose={() => setOnboarding(false)} />}
      {isDemo && <AvvisoSmartphone />}
      {isDemo && !isAdmin && ccu && <Benvenuto user={user} hotelName={demo.hotel_name} onNav={setNav} />}
      <Toaster position="top-right" richColors />
    </div>
  );
}
